import Pagination from "../../components/ui/Pagination";
import StatusAction from "../../components/ui/StatusAction";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Reuse the existing Products page styles (tables, dropdowns, pagination, etc.)
import "../ProductsPage/ProductsPage.css";

import {
  getOrders,
  updateOrderStatus,
} from "../../services/purchaseOrderService";
import { Order } from "../../types/order";
import PageLoader from "../../compoonents/PageLoader";
import {
  PageFilterDropdown,
  PageSearchBar,
  PageToolbar,
} from "../../compoonents/PageToolbar";

import { FiEye } from "react-icons/fi";


// Backend enum values (EShopForMeStatusType) are lowercase — send these exact values.
const SHOP_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "request", label: "Request" },
  { value: "successful", label: "Successful" },
  { value: "failed", label: "Failed" },
  { value: "payment_confirmed", label: "Payment Confirmed" },
  { value: "payment_cancelled", label: "Payment Cancelled" },
  { value: "refund_processing", label: "Refund Processing" },
  { value: "refund_confirmed", label: "Refund Confirmed" },
  { value: "shopping_ongoing", label: "Shopping Ongoing" },
  { value: "product_checked_in", label: "Product Checked In" },
  { value: "arrived_at_hub", label: "Arrived At Hub" },
  { value: "delivered", label: "Delivered" },
  { value: "handled_by_user", label: "Handled By User" },
];

const formatShopStatus = (status: string): string => {
  const match = SHOP_STATUS_OPTIONS.find(
    (o) => o.value.toLowerCase() === (status || "").toLowerCase(),
  );
  return match ? match.label : (status || "N/A").replace(/_/g, " ");
};

const getStatusColor = (status: string): string => {
  switch ((status || "").toLowerCase()) {
    case "request":
    case "product_checked_in":
      return "bg-blue-100 text-blue-800";
    case "successful":
    case "payment_confirmed":
    case "refund_confirmed":
    case "delivered":
      return "bg-green-100 text-green-800";
    case "failed":
    case "payment_cancelled":
      return "bg-red-100 text-red-800";
    case "refund_processing":
      return "bg-yellow-100 text-yellow-800";
    case "shopping_ongoing":
      return "bg-purple-100 text-purple-800";
    case "arrived_at_hub":
      return "bg-indigo-100 text-indigo-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// Status dropdown for a single order row
const ShopStatusDropdown: React.FC<{ currentStatus: string; onStatusUpdate: (newStatus: string) => Promise<void>; }> = ({ currentStatus, onStatusUpdate }) => (
  <StatusAction value={currentStatus} label={formatShopStatus(currentStatus)} badgeClassName={getStatusColor(currentStatus)} options={SHOP_STATUS_OPTIONS}
    onChange={async status => {
      try { await onStatusUpdate(status); toast.success("Status updated"); }
      catch { toast.error("Failed to update status"); }
    }} />
);

const ShopWithLinkPage: React.FC = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response = await getOrders(
          currentPage,
          10,
          searchTerm,
          filterStatus,
        );
        setOrders(response.data?.allOrders || []);
        setTotalPages(response.data?.pagination?.totalPages ?? 1);
      } catch (err) {
        console.error("Failed to fetch shop orders:", err);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => fetchOrders(), 400);
    return () => clearTimeout(timer);
  }, [currentPage, searchTerm, filterStatus]);

  const visibleOrders = useMemo(() => {
    return orders.filter((o) => {
      const searchLower = searchTerm.toLowerCase();
      const name = `${o.user?.firstName ?? ""} ${
        o.user?.lastName ?? ""
      }`.toLowerCase();
      const email = (o.user?.email ?? "").toLowerCase();
      const matchSearch =
        name.includes(searchLower) ||
        email.includes(searchLower) ||
        o.id.toLowerCase().includes(searchLower);

      const matchStatus = filterStatus
        ? (o.status || "").toLowerCase() === filterStatus.toLowerCase()
        : true;
      return matchSearch && matchStatus;
    });
  }, [orders, searchTerm, filterStatus]);

  return (
    <div className="products-page">

      <header className="page-header">
        <h3>Shop With Link</h3>
        <div className="page-controls">
          <PageToolbar>
            <PageFilterDropdown
              value={filterStatus}
              options={SHOP_STATUS_OPTIONS}
              onChange={setFilterStatus}
            />
            <PageSearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search orders..."
            />
          </PageToolbar>
        </div>
      </header>

      <div className="page-content-wrapper">
        <div className="product-content">
          {loading ? (
            <PageLoader message="Loading orders..." />
          ) : visibleOrders.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#666" }}
            >
              No orders found.
            </div>
          ) : (
            <div className="table-container admin-table-container">
              <table className="data-table admin-data-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer Name</th>
                    <th>Email</th>
                    <th>Date Created</th>
                    <th>Items</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map((order) => (
                    <tr key={order.id}>
                      <td data-label="Order ID">
                        {order.id.substring(0, 8)}...
                      </td>
                      <td data-label="Customer Name">{`${
                        order.user?.firstName ?? ""
                      } ${order.user?.lastName ?? ""}`}</td>
                      <td data-label="Email">{order.user?.email}</td>
                      <td data-label="Date Created">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>
                      <td data-label="Items">{order.items?.length ?? 0}</td>
                      <td data-label="Status">
                        <ShopStatusDropdown
                          currentStatus={order?.status}
                          onStatusUpdate={async (newStatus) => {
                            await updateOrderStatus(order.id, newStatus);
                            setOrders((curr) =>
                              curr.map((o) =>
                                o.id === order.id
                                  ? { ...o, status: newStatus }
                                  : o,
                              ),
                            );
                          }}
                        />
                      </td>
                      <td data-label="Action">
                        <div className="table-action-buttons">
                          <button
                            type="button"
                            className="table-action-btn"
                            title="View order"
                            aria-label="View order"
                            onClick={() =>
                              navigate(`/shop-with-link/${order.id}`)
                            }
                          >
                            <FiEye />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && visibleOrders.length > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} loading={loading} />
        )}
      </div>
    </div>
  );
};

export default ShopWithLinkPage;
